import React from 'react'
import type { PlotViewport } from '../contract'
import { usePlotGraphicBudget } from '../_shared/usePlotGraphicBudget'

import { QuadrantChart } from '../../components/charts/QuadrantChart/QuadrantChart'
import { QuadrantCornerLabels } from '@digital-twins/slide-schemas/generated/plot-types'
import { PlotPropsOf, StoredPlotPayload } from '../contract'
import { QUADRANT_PLANE_MIDPOINT } from './adapters'
import { QuadrantSlideMap, QuadrantSlideMapLabelArgs, slideMapFromPlot } from './QuadrantSlideMap'

/**
 * QuadrantPlot: the `quadrant` kind at card size (summary card, quick-study),
 * dot style. Plots plan O. With `variant="slide-map"` it draws the deck map
 * instead (`QuadrantSlideMap`, plots Wave 7 D1): point cards, corner pills,
 * crosshair and axis titles on the slide's frozen 873x667 frame. Only the deck
 * quadrant-map slide passes that variant; a stored `props.size` never picks it,
 * so a payload stamped with `size: 'slide'` still draws the card everywhere else
 * (the chart-segment breakdown passes the stored props through).
 *
 * Draws Python's stamps and nothing else: every point on the 0-100 plane, the
 * crosshair at the stamped `midpoint`, the four corner names only when all four
 * are given. A point with a null axis is not drawn (never at 0); when the
 * payload discloses it (`truncation`) the "shown of total" note sits above the
 * map, never on a mark. Colours: `neutral` (one token for every dot, the
 * default: a quadrant is not a verdict) or `byItem` (the stamped `colorSlot`).
 * No word is uppercased.
 */

const PLANE_MIN = 0
const PLANE_MAX = 100
const NEUTRAL_DOT = 'var(--slide-chart-1)'
const UNSLOTTED_DOT = 'var(--slide-text-muted)'

export interface QuadrantPlotProps {
  viewport?: PlotViewport
  data: StoredPlotPayload<'quadrant'>['data']
  props?: PlotPropsOf<'quadrant'>
  /**
   * Corner names the surface resolved itself (a summary card translates the
   * recipe keys in the viewer's UI language). Wins over `data.cornerLabels`.
   */
  cornerLabels?: QuadrantCornerLabels | null
  /**
   * The "{shown} of {total}" note template the surface resolved itself (a
   * summary card reads it in the viewer's UI language). Wins over
   * `data.labels.n_of_total_shown`.
   */
  shownOfTotalTemplate?: string | null
  /** Root test id; children derive theirs from it. */
  testId?: string
  /**
   * `'slide-map'`: the deck slide's map (the quadrant-map slide alone passes it).
   * Defaults to the card. Chosen by the caller, never by the stored props.
   */
  variant?: 'card' | 'slide-map'
  /** `variant="slide-map"` only: the deck passes its editable text for a card's name. */
  renderPointLabel?: (args: QuadrantSlideMapLabelArgs) => React.ReactNode
  /**
   * Card only: stack the names of points that land on one spot instead of
   * overprinting them. Off by default, so a stored card or quick-study map
   * draws as it always has; the chart-segment tabs (live views) turn it on.
   * The slide map places its cards apart on its own.
   */
  stackCoincident?: boolean
}

/** Points closer than this on the 0-100 plane share a spot (the gear's old rule). */
const COINCIDENT_CELL = 4

/**
 * Each drawn point's place in the stack of names at its spot, in draw order:
 * 0 for the first point in a cell of the plane, 1 for the next, and so on.
 */
export function coincidentStackIndex(points: ReadonlyArray<{ name: string; x: number; y: number }>): Record<string, number> {
  const seen = new Map<string, number>()
  const byName: Record<string, number> = {}
  for (const point of points) {
    const key = `${Math.round(point.x / COINCIDENT_CELL)}:${Math.round(point.y / COINCIDENT_CELL)}`
    const index = seen.get(key) ?? 0
    byName[point.name] = index
    seen.set(key, index + 1)
  }
  return byName
}

function fillShownOfTotal(template: string, shown: number, total: number): string {
  return template.replace('{shown}', String(shown)).replace('{total}', String(total))
}

function dotName(point: { label: string; badge?: string | null }): string {
  return point.badge ? `${point.badge}  ${point.label}` : point.label
}

export function QuadrantPlot(props: QuadrantPlotProps) {
  const { data, props: renderProps, cornerLabels, shownOfTotalTemplate, renderPointLabel, variant = 'card', stackCoincident = false } = props
  const rootRef = React.useRef<HTMLDivElement>(null)
  const graphicRef = React.useRef<HTMLDivElement>(null)
  const graphics = React.useMemo(() => [graphicRef], [])
  const budget = usePlotGraphicBudget(props.viewport, rootRef, graphics)
  const [minimum, setMinimum] = React.useState({ width: 0, height: 0, feasible: true })
  const measureMinimum = React.useCallback((next: { width: number; height: number; feasible: boolean }) => {
    setMinimum((previous) => previous.width === next.width && previous.height === next.height && previous.feasible === next.feasible ? previous : next)
  }, [])
  const testId = props.testId ?? (variant === 'slide-map' ? 'quadrant-map-plot' : 'quadrant-plot')
  const hidesCorners = renderProps?.showCornerLabels === false
  // One slide model per stored plot, so the card placement is not recomputed on every render.
  const slideModel = React.useMemo(() => {
    if (variant !== 'slide-map') {
      return null
    }
    const model = slideMapFromPlot(data)
    return { ...model, cornerLabels: hidesCorners ? null : cornerLabels ?? model.cornerLabels }
  }, [variant, data, hidesCorners, cornerLabels])

  if (variant === 'slide-map' && slideModel) {
    // The slide's frame has no room for the shown-of-total note: the deck's
    // rail footnote says which items the map shows.
    return (
      <QuadrantSlideMap
        model={slideModel}
        renderMode={renderProps?.renderMode}
        testId={testId}
        renderPointLabel={renderPointLabel}
        // A long x-axis title wraps between the bottom corner names instead of
        // printing under them (Wave 7 D1); a title that fits does not move.
        keepXTitleClearOfCorners
      />
    )
  }

  const colorMode = renderProps?.colorMode ?? 'neutral'
  const midpoint = data.midpoint ?? QUADRANT_PLANE_MIDPOINT
  const drawn = data.points.filter(
    (point): point is typeof point & { x: number; y: number } =>
      typeof point.x === 'number' && typeof point.y === 'number',
  )
  const corners = renderProps?.showCornerLabels === false ? null : cornerLabels ?? data.cornerLabels ?? null

  const items = drawn.map((point) => ({ name: dotName(point), x: point.x, y: point.y }))
  const dotColorsByName = Object.fromEntries(
    drawn.map((point) => [
      dotName(point),
      colorMode === 'byItem'
        ? point.colorSlot
          ? `var(--slide-chart-${point.colorSlot})`
          : UNSLOTTED_DOT
        : NEUTRAL_DOT,
    ]),
  )
  const labelStackByName = stackCoincident ? coincidentStackIndex(items) : undefined
  const template = shownOfTotalTemplate ?? data.labels.n_of_total_shown
  const truncation = data.truncation
  const note =
    truncation && template && truncation.shown < truncation.total
      ? fillShownOfTotal(template, truncation.shown, truncation.total)
      : null

  return (
    <div ref={rootRef} data-testid={testId} data-viewport-infeasible={budget ? !minimum.feasible || budget.width < minimum.width || budget.height < minimum.height : undefined} className="flex flex-col w-full min-w-0" style={{ height: props.viewport ? 'auto' : '100%' }}>
      {note ? (
        <div
          data-testid={`${testId}-note`}
          style={{ fontSize: 'var(--font-size-caption)', color: 'var(--slide-text-muted)', marginBottom: 'var(--spacing-1)' }}
        >
          {note}
        </div>
      ) : null}
      <div ref={graphicRef} data-testid={`${testId}-map`} className="relative" style={props.viewport ? { height: Math.max(minimum.height, budget?.height ?? props.viewport.maxHeight) } : { flex: '1 1 auto', minHeight: 0 }}>
        <QuadrantChart
          onMinimumSize={props.viewport ? measureMinimum : undefined}
          items={items}
          xAxisLabel={data.axes.x.label}
          yAxisLabel={data.axes.y.label}
          xAxisFullLabel={data.axes.x.fullLabel}
          yAxisFullLabel={data.axes.y.fullLabel}
          renderMode={renderProps?.renderMode}
          xScaleMin={PLANE_MIN}
          xScaleMax={PLANE_MAX}
          yScaleMin={PLANE_MIN}
          yScaleMax={PLANE_MAX}
          xMidpoint={midpoint.x}
          yMidpoint={midpoint.y}
          quadrantLabels={corners ?? undefined}
          // No watermark: the corner names (when the recipe gave them) are the
          // only quadrant names, and an unnamed map names none.
          backgroundQuadrantLabels={{}}
          dotColorsByName={dotColorsByName}
          showInlineLabels
          labelStackByName={labelStackByName}
        />
      </div>
    </div>
  )
}
